import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Dropdown, ButtonGroup } from "react-bootstrap";

// Components
import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import GridVehicleActivities from "../Components/Grid/GridVehicleActivities"
import MultiSelectCheck from '../Components/MultiSelectCheck/MultiSelectCheck'
import DatePickModal from '../Components/Modals/DatePickModal'

// Services
import { downloadService } from "../../Services/download.service";
import { reportService } from "../../Services/report.service";
import { toDateISOString, stringToDate } from '../../Services/string.service';
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { Filters } from "../../components/Filters";
import { useTranslation } from "react-i18next";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { vehicleService } from "../../Services/vehicle.service";

export default function ReportVehicleActivity({ pageTitle }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    React.useEffect(() => {
        dispatch({ type: "title", value: pageTitle || t("REPORTS.VEHICLE_ACTIVITY_TITLE") });
    }, [dispatch, t, pageTitle]);
    const intervalRef = useRef(null);
    const objGridRef = useRef(null);

    const [toastParams, setToastParams] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [gridInfo, setGridInfo] = useState([])
    const [pageSize, setPageSize] = useState(7)
    const [pageNumber, setPageNumber] = useState(1)
    const [speedFilter, setSpeedFilter] = useState(40)
    const [initialDate, setInitialDate] = useState(toDateISOString(new Date()).replace("Z", ""));
    const [endDate, setEndDate] = useState(toDateISOString(new Date()).replace("Z", ""));
    const [initialTime, setInitialTime] = useState('00:00');
    const [endTime, setEndTime] = useState('23:59');
    const [totalLines, setTotalLines] = useState(0)
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicles, setSelectedVehicles] = useState([])
    const [modalDateShow, setModalDateShow] = useState(false)
    const [personalDates, setPersonalDates] = useState([])
    const [optionVehicles, setOptionVehicles] = useState([]);
    const [reportId, setReportId] = useState(0);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [separateSheets, setSeparateSheets] = useState(false);

    const fetchVehicleGroups = async () => {
        try {
            const groups = await vehicleGroupService.fetchGroups();

            if (Array.isArray(groups)) {
                setOptionVehicles(groups);

                if (groups.length > 0) {
                    setSelectedGroup(groups[0].vehicleGroupId);
                }
            } else {
                setOptionVehicles([]);
                if (groups?.error) {
                    console.log("Erro ao buscar grupos:", groups.message || groups.friendlyMessage);
                }
            }
        } catch (error) {
            console.log(error);
            setOptionVehicles([]);
        } finally {
            setIsLoading(false);
        }
    };

    const initialize = async () => {
        setIsLoading(true);
        try {
            const vehicles = await vehicleService.fetchVehicles(Number(selectedGroup));
            setVehicles(vehicles || []);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadInitialReport = async () => {
        try {
            setIsLoading(true);
            const objGrid = {
                dateStart: stringToDate(initialDate),
                dateEnd: stringToDate(endDate),
                hourStart: initialTime,
                hourEnd: endTime,
                velocity: speedFilter,
                listVehicleId: selectedVehicles,
                listPersonalDate: personalDates,
            };

            objGridRef.current = objGrid;

            const responseGrid = await reportService.buildReportVehicles(objGrid);

            if (responseGrid) {
                setGridInfo(responseGrid);
                setReportId(responseGrid.reportId || 0);
            } else {
                setGridInfo([]);
            }
        } catch (error) {
            console.error(`Erro ao carregar relatório inicial:`, error);
            setGridInfo([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        initialize();
        fetchVehicleGroups();
    }, []);

    // Recarrega veículos quando o grupo selecionado mudar
    useEffect(() => {
        if (selectedGroup) {
            initialize();
        }
    }, [selectedGroup]);

    // Carrega o relatório inicial assim que os dados estão prontos
    useEffect(() => {
        if (vehicles.length > 0) {
            loadInitialReport();
        }
    }, [vehicles])

    useEffect(() => {
        handleFilter()
        // eslint-disable-next-line
    }, [pageNumber]) /** eslint-disable-next-line */

    const handleFilter = async () => {
        try {
            setToastParams(JSON.stringify({ show: false }));
            setIsLoading(true);

            const response = await reportService.list(pageSize, pageNumber);

            if (response) {
                setGridInfo(response.reportRequest)
                setTotalLines(response.totalLines)
            } else {
                setGridInfo([])
                setTotalLines(0)
            }
        } catch (error) {
            console.error(`Erro ao buscar dados do relatório ${pageTitle}:`, error);
            setGridInfo([])
            setTotalLines(0)
        } finally {
            setIsLoading(false);
        }
    };

    const generateReport = async () => {
        try {
            if (
                initialDate === '' ||
                endDate === '' ||
                initialTime === '' ||
                endTime === ''
            ) {
                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: 'Campos inválidos',
                        body: 'Preencha todos os campos do filtro.',
                        color: 'warning',
                    }),
                );
                setIsLoading(false);
                return;
            }

            const startDate = new Date(initialDate);
            const endDateObj = new Date(endDate);

            if (startDate > endDateObj) {
                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: 'Período inválido',
                        body: 'A data inicial não pode ser maior que a data final.',
                        color: 'warning',
                    }),
                );
                setIsLoading(false);
                return;
            }

            if (startDate.getTime() === endDateObj.getTime() && initialTime > endTime) {
                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: 'Período inválido',
                        body: 'O horário inicial não pode ser maior que o horário final.',
                        color: 'warning',
                    }),
                );
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            const objReport = {
                startDate: initialDate,
                endDate: endDate,
                startProfessional: initialTime,
                endProfessional: endTime,
                maxSpeed: Number(speedFilter),
                vehicleIds: selectedVehicles,
                personalDates: personalDates
            };

            const responseReport = await reportService.addBuildReportVehicles(objReport);
            const reportIdFromApi = responseReport ?? 0;
            setReportId(reportIdFromApi);

            setToastParams(
                JSON.stringify({
                    show: true,
                    title: 'Sucesso',
                    body: 'Relatório gerado com sucesso',
                    color: 'success',
                }),
            );

            // Limpa intervalo anterior se existir
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            // Salva objGrid para usar no intervalo
            const objGrid = {
                dateStart: stringToDate(initialDate),
                dateEnd: stringToDate(endDate),
                hourStart: initialTime,
                hourEnd: endTime,
                velocity: speedFilter,
                listVehicleId: selectedVehicles,
                listPersonalDate: personalDates,
            };
            objGridRef.current = objGrid;

            // Inicia novo intervalo
            intervalRef.current = setInterval(async () => {
                console.log('🔄 Atualizando relatório automaticamente...');
                try {
                    const response = await reportService.buildReportVehicles(objGridRef.current);
                    if (response) {
                        setGridInfo(response);
                    }
                } catch (error) {
                    console.error('Erro ao atualizar relatório automaticamente:', error);
                }
            }, 5000);
        } catch (error) {
            console.error('Erro ao gerar o relatório:', error);
            setToastParams(
                JSON.stringify({
                    show: true,
                    title: 'Erro ao gerar',
                    body: 'Não foi possível gerar o relatório.',
                    color: 'warning',
                }),
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Recarrega veículos quando o grupo selecionado mudar
    useEffect(() => {
        if (selectedGroup) {
            setSelectedVehicles([]); // Limpa os veículos selecionados
            initialize();
        }
    }, [selectedGroup]);


    // Limpa o intervalo quando o componente for desmontado
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    const handleDownload = async (data) => {
        try {
            setIsLoading(true);

            const response = await reportService.vehiclesActivityDownload("xls", data.reportId, separateSheets);

            if (response) {
                downloadService.downloadBase64File(response, 'xls', t("REPORTS.VEHICLE_ACTIVITY_TITLE"));
            } else {
                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: "Erro ao gerar",
                        body: "Não foi possivel gerar o relatorio.",
                        color: "warning",
                    })
                );
            }
        } catch (error) {
            console.error(`Erro ao gerar o relatorio:`, error);
        } finally {
            setIsLoading(false);
        }
    }

    const cleanFilter = () => {
        setSelectedVehicles([])
        setSpeedFilter(0)
        setInitialTime('');
        setEndTime('');
        setInitialDate('');
        setEndDate('');
    }

    const closeModalDate = (val) => {
        const newDates = val.map((x) => new Date(x));
        setPersonalDates(newDates);
        setModalDateShow(false);
    };

    return (
        <div className="container-xxl flex-grow-1 container-p-y">
            <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />
            
            {/* Filtro de Grupos no topo */}
            <div className="row mb-3">
                <div className="col-md-5 col-12">
                    <Filters
                        id={"veiculos"}
                        isMulti={false}
                        title={t("TITLE.FILTER_GROUPS")}
                        pageTitle={"globalHeader"}
                        hasSubTitle={true}
                        subTitle={"Todos os grupos"}
                        options={optionVehicles}
                        icon={HiOutlineRectangleGroup}
                        hasIcon={true}
                        onChange={(selectedValue) => {
                            setSelectedGroup(selectedValue);
                        }}
                    />
                </div>
            </div>

            {/* MultiSelectCheck e Velocidade Máxima lado a lado */}
            <div className="row mb-2">
                <div className="col-md-12 col-12">
                    <div className="row">
                        {/* MultiSelectCheck */}
                        <div className="col-md-6 col-12">
                            <MultiSelectCheck
                                listItems={vehicles}
                                itemLabel={'plate'}
                                itemId={'vehicleId'}
                                title={'Veículos'}
                                selectedItems={selectedVehicles}
                                setSelectedItems={setSelectedVehicles}
                            />
                        </div>

                        {/* Velocidade Máxima e outros filtros */}
                        <div className="col-md-6 col-12">
                            <div className="row mb-2">
                                <div className="col-12">
                                    <div className="input-group">
                                        <span className="input-group-text text-primary">
                                            Velocidade Máxima
                                        </span>
                                        <select
                                            name="vehicles"
                                            className="form-control"
                                            value={speedFilter}
                                            disabled={isLoading}
                                            onChange={(e) => setSpeedFilter(Number(e.target.value))}
                                        >
                                            <>
                                                {Array.from({ length: 10 }, (_, index) => 40 + index * 10).map((value) => (
                                                    <option key={value} value={value}>
                                                        {value}
                                                    </option>
                                                ))}
                                            </>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-6">
                                    <div className="input-group">
                                        <span className="input-group-text text-primary">Período Início</span>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={initialDate}
                                            onChange={(e) => setInitialDate(e.target.value)}
                                            disabled={isLoading}
                                            id="filter-initial-date"
                                        />
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="input-group">
                                        <span className="input-group-text text-primary">Período Fim</span>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            disabled={isLoading}
                                            id="filter-end-date"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <div className="input-group">
                                        <span className="input-group-text text-primary">Horário Início</span>
                                        <input
                                            className="form-control"
                                            type="time"
                                            value={initialTime}
                                            disabled={isLoading}
                                            onChange={(e) => setInitialTime(e.target.value)}
                                            id="filter-initial-time"
                                        />
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="input-group">
                                        <span className="input-group-text text-primary">Horário Fim</span>
                                        <input
                                            className="form-control"
                                            type="time"
                                            disabled={isLoading}
                                            value={endTime}
                                            onChange={(e) => setEndTime(e.target.value)}
                                            id="filter-end-time"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                    <span>Ao gerar a planilha, deseja que os veículos sejam separados em abas?</span>
                    <select value={separateSheets} onChange={(e) => setSeparateSheets(e.target.value === 'true')} className="form-select" style={{width: 'auto'}}>
                        <option value="true">Sim</option>
                        <option value="false">Não</option>
                    </select>
                </div>
                <Dropdown as={ButtonGroup}>
                    <Dropdown.Toggle
                        id="dropdown-custom-button"
                        variant="primary"
                        className="w-100 w-md-auto"
                    >
                        Ações
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={generateReport}>
                            Solicitar
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setModalDateShow(true)}>
                            Uso Pessoal
                        </Dropdown.Item>
                        <Dropdown.Item onClick={cleanFilter}>
                            Limpar Filtro
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            {isLoading ?
                (
                    <Spinner />
                ) : (
                    <GridVehicleActivities
                        gridData={gridInfo}
                        isLoading={isLoading}
                        handleDownload={handleDownload}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        paginateIndex={pageNumber}
                        setPaginateIndex={setPageNumber}
                        totalLines={totalLines}
                    />
                )}
            <DatePickModal
                reportId={reportId}
                show={modalDateShow}
                dateValue={personalDates}
                title={'Data de uso pessoal'}
                handleClose={closeModalDate}
            />
        </div>
    );
}
